import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, MicOff, Mic, UserMinus, Ban, Skull, Brush } from 'lucide-react';
import { api, avatarUrl, formatDuration, timeAgo } from '../api.js';
import { useGuild } from './GuildLayout.jsx';

export default function MemberDetail() {
  const { guildId } = useGuild();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => { reload(); }, [userId]);

  function reload() {
    setData(null);
    api.userDetail(guildId, userId).then(setData).catch(err => setData({ error: err.message }));
  }

  if (!data) return <div className="empty">Loading…</div>;
  if (data.error) return <div className="empty">Error: {data.error}</div>;

  const user = data.member?.user;
  const isTimedOut = data.member?.communication_disabled_until && new Date(data.member.communication_disabled_until) > new Date();

  async function runAction(fn, success) {
    try {
      const result = await fn();
      const msg = typeof result === 'string' ? result : success;
      setToast({ type: 'success', msg });
      reload();
    } catch (err) { setToast({ type: 'error', msg: err.message }); }
    finally { setModal(null); setTimeout(() => setToast(null), 2500); }
  }

  return (
    <>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: 0 }}>
          {user ? <img className="avatar lg" src={avatarUrl(user)} alt="" /> : <div className="avatar lg" />}
        </div>
        <div>
          <h1 style={{ marginBottom: 4 }}>
            {data.member?.nick || user?.global_name || user?.username || 'Unknown user'}
          </h1>
          <div className="dim">{user?.username || <span className="badge danger">left server</span>} · <span className="tag">{userId}</span></div>
          {data.member?.joined_at && <div className="muted" style={{ marginTop: 4 }}>Joined {new Date(data.member.joined_at).toLocaleDateString()}</div>}
          {isTimedOut && <div style={{ marginTop: 6 }}><span className="badge warn">timed out until {new Date(data.member.communication_disabled_until).toLocaleString()}</span></div>}
        </div>
      </div>

      {data.member && (
        <div className="row" style={{ marginTop: '1.5rem', justifyContent: 'flex-start', gap: '.5rem', flex: 0 }}>
          <button className="warn" onClick={() => setModal({ type: 'warn' })}><AlertTriangle size={14} strokeWidth={2.5} /> Warn</button>
          {isTimedOut
            ? <button className="ghost" onClick={() => runAction(() => api.untimeout(guildId, { user_id: userId, reason: 'untimeout from dashboard' }), 'Timeout removed')}><Mic size={14} strokeWidth={2.5} /> Remove timeout</button>
            : <button className="warn" onClick={() => setModal({ type: 'timeout' })}><MicOff size={14} strokeWidth={2.5} /> Timeout</button>}
          <button className="danger" onClick={() => setModal({ type: 'kick' })}><UserMinus size={14} strokeWidth={2.5} /> Kick</button>
          <button className="danger" onClick={() => setModal({ type: 'ban' })}><Ban size={14} strokeWidth={2.5} /> Ban</button>
          <button className="warn" title="Delete this user's recent messages across every channel" onClick={() => setModal({ type: 'cleanup' })}><Brush size={14} strokeWidth={2.5} /> Cleanup</button>
          <button className="danger" title="One-click: ban + delete all messages from the last 7 days + DM" onClick={() => {
            if (!confirm(`Spam Hammer <@${userId}>?\n\nThis bans them, deletes ALL their messages from the last 7 days across every channel, and DMs them. Use for spam-bot accounts only.`)) return;
            runAction(() => api.spamHammer(guildId, { user_id: userId, reason: 'Spam — wiped via Spam Hammer' }), 'Spam-hammered');
          }}><Skull size={14} strokeWidth={2.5} /> Spam Hammer</button>
        </div>
      )}

      <h3 style={{ marginTop: '2rem' }}>Warnings ({data.warnings.length})</h3>
      {data.warnings.length === 0 ? <div className="empty">No warnings.</div> : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>When</th><th>Moderator</th><th>Reason</th></tr></thead>
            <tbody>
              {data.warnings.map(w => (
                <tr key={w.id}>
                  <td>{timeAgo(w.created_at)}</td>
                  <td className="tag">{w.moderator_id}</td>
                  <td>{w.reason || <span className="dim">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ marginTop: '2rem' }}>Moderation history ({data.actions.length})</h3>
      {data.actions.length === 0 ? <div className="empty">No actions taken against this user.</div> : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>When</th><th>Action</th><th>Moderator</th><th>Reason</th></tr></thead>
            <tbody>
              {data.actions.map(a => (
                <tr key={a.id}>
                  <td>{timeAgo(a.created_at)}</td>
                  <td>
                    <span className="badge">{a.action}</span>
                    {a.duration_ms && <span className="dim" style={{ marginLeft: 6 }}>{formatDuration(a.duration_ms)}</span>}
                  </td>
                  <td className="tag">{a.moderator_id}</td>
                  <td>{a.reason || <span className="dim">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InviteSection guildId={guildId} userId={userId} />

      <h3 style={{ marginTop: '2rem' }}>Join / leave history ({data.events.length})</h3>
      {data.events.length === 0 ? <div className="empty">No join/leave events recorded.</div> : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>When</th><th>Event</th></tr></thead>
            <tbody>
              {data.events.map(e => (
                <tr key={e.id}>
                  <td>{timeAgo(e.created_at)}</td>
                  <td><span className={`badge ${e.event_type === 'leave' ? 'danger' : 'success'}`}>{e.event_type}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && <ActionModal type={modal.type} onCancel={() => setModal(null)} onSubmit={(body) => {
        const map = {
          warn: () => api.warn(guildId, { user_id: userId, ...body }),
          timeout: () => api.timeout(guildId, { user_id: userId, ...body }),
          kick: () => api.kick(guildId, { user_id: userId, ...body }),
          ban: () => api.ban(guildId, { user_id: userId, ...body }),
          cleanup: async () => {
            const r = await api.cleanup(guildId, { user_id: userId, hours: body.hours });
            return `Deleted ${r.deleted} message(s) across ${r.channelsHit} channel(s)${r.channelsSkipped ? ` · ${r.channelsSkipped} channels skipped (no permission)` : ''}`;
          },
        };
        const labels = { warn: 'Warning issued', timeout: 'Timed out', kick: 'Kicked', ban: 'Banned', cleanup: 'Cleanup complete' };
        runAction(map[modal.type], labels[modal.type]);
      }} />}

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}

function InviteSection({ guildId, userId }) {
  const [info, setInfo] = useState({ loading: true });
  useEffect(() => {
    setInfo({ loading: true });
    Promise.all([api.invitedBy(guildId, userId), api.inviteesOf(guildId, userId)])
      .then(([by, from]) => setInfo({ loading: false, by: by.row, invitees: from.invitees }))
      .catch(err => setInfo({ loading: false, error: err.message }));
  }, [guildId, userId]);

  if (info.loading || info.error) return null;

  const inviteeCount = info.invitees?.length || 0;

  return (
    <>
      <h3 style={{ marginTop: '2rem' }}>Invite attribution</h3>
      <div className="card">
        <div className="muted" style={{ marginBottom: inviteeCount ? '1rem' : 0 }}>
          {info.by
            ? info.by.is_vanity
              ? <>Joined via the <span className="badge accent">vanity URL</span>.</>
              : info.by.inviter_id
                ? <>Invited by <Link to={`/g/${guildId}/users/${info.by.inviter_id}`} className="tag">{info.by.inviter_id}</Link>{info.by.invite_code && <> using invite <span className="tag">{info.by.invite_code}</span></>}.</>
                : <>Joined with an unknown invite (bot may have been offline).</>
            : <>No invite record for this user.</>}
        </div>
        {inviteeCount > 0 && (
          <>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Has invited <span className="badge accent">{inviteeCount}</span> {inviteeCount === 1 ? 'member' : 'members'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
              {info.invitees.slice(0, 25).map(inv => (
                <Link key={inv.id} className="tag" to={`/g/${guildId}/users/${inv.invitee_id}`}>{inv.invitee_id}</Link>
              ))}
              {info.invitees.length > 25 && <span className="dim" style={{ fontSize: 12, alignSelf: 'center' }}>+{info.invitees.length - 25} more</span>}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function ActionModal({ type, onSubmit, onCancel }) {
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('10m');
  const [deleteDays, setDeleteDays] = useState(0);
  const [hours, setHours] = useState(1);

  const isCleanup = type === 'cleanup';

  function submit(e) {
    e.preventDefault();
    if (isCleanup) { onSubmit({ hours: Number(hours) }); return; }
    const body = { reason };
    if (type === 'timeout') {
      const ms = parseDur(duration);
      if (!ms) return alert('Invalid duration. Try 10m, 1h, 2d.');
      body.duration_ms = ms;
    }
    if (type === 'ban') body.delete_days = Number(deleteDays);
    onSubmit(body);
  }

  const titleMap = { cleanup: 'Cleanup recent messages' };
  const submitMap = { cleanup: 'Run cleanup' };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>{titleMap[type] || `${type.charAt(0).toUpperCase()}${type.slice(1)} user`}</h3>
        <form onSubmit={submit}>
          {isCleanup ? (
            <>
              <p className="muted" style={{ marginTop: 0 }}>SoupOS will scan every text channel it has Manage Messages on and bulk-delete this user's posts inside the window. Limited to Discord's 14-day bulk-delete cap.</p>
              <div className="field">
                <label>How many hours back? (1–168)</label>
                <input type="number" min="1" max="168" value={hours} onChange={e => setHours(e.target.value)} autoFocus />
              </div>
            </>
          ) : (
            <>
              <div className="field">
                <label>Reason</label>
                <input value={reason} onChange={e => setReason(e.target.value)} autoFocus />
              </div>
              {type === 'timeout' && (
                <div className="field">
                  <label>Duration (10m, 1h, 2d, max 28d)</label>
                  <input value={duration} onChange={e => setDuration(e.target.value)} />
                </div>
              )}
              {type === 'ban' && (
                <div className="field">
                  <label>Delete message history (days, 0–7)</label>
                  <input type="number" min="0" max="7" value={deleteDays} onChange={e => setDeleteDays(e.target.value)} />
                </div>
              )}
            </>
          )}
          <div className="row actions" style={{ gap: '.5rem' }}>
            <button type="button" className="ghost" onClick={onCancel}>Cancel</button>
            <button type="submit" className={type === 'warn' || type === 'timeout' || type === 'cleanup' ? 'warn' : 'danger'}>
              {submitMap[type] || `${type.charAt(0).toUpperCase()}${type.slice(1)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const UNITS = { s: 1000, m: 60000, h: 3600000, d: 86400000, w: 604800000 };
function parseDur(s) {
  let total = 0, matched = false;
  for (const m of s.matchAll(/(\d+)\s*([smhdw])/gi)) {
    matched = true; total += Number(m[1]) * UNITS[m[2].toLowerCase()];
  }
  return matched ? total : null;
}
